import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";

/**
 * A blank line in a stored Protokoll entry: a paragraph whose only content is
 * U+00A0 (no-break space).
 *
 * prosemirror-markdown writes nothing for an empty paragraph and collapses any
 * run of them into one paragraph break, so a blank line the writer typed is
 * gone after saving. A paragraph holding one NBSP survives the round trip (it
 * serializes as its own block and renders as `<p>&nbsp;</p>`). The memo editor
 * turns these paragraphs back into truly empty ones on load.
 */
export const BLANK_LINE_MARKER = "\u00A0";

const LIST_TYPES = new Set(["bulletList", "orderedList"]);

function isEmptyParagraph(node: ProseMirrorNode | undefined): boolean {
  return node !== undefined && node.type.name === "paragraph" && node.childCount === 0;
}

/** A list with its empty items removed, or null when no item is left. */
function pruneList(list: ProseMirrorNode): ProseMirrorNode | null {
  const items: ProseMirrorNode[] = [];
  list.forEach((item) => {
    const kept = pruneListItem(item);
    if (kept) items.push(kept);
  });
  return items.length > 0 ? list.copy(Fragment.fromArray(items)) : null;
}

/**
 * A list item with its nested lists pruned, or null when all that is left is
 * one empty paragraph.
 *
 * Bottom-up on purpose: an item whose nested list held only empty items is
 * judged AFTER that list is gone, so it is dropped too instead of being stored
 * as an empty bullet. An item whose empty paragraph carries a nested list with
 * content is kept, because that list is the writer's content.
 */
function pruneListItem(item: ProseMirrorNode): ProseMirrorNode | null {
  if (item.type.name !== "listItem") return item;
  const children: ProseMirrorNode[] = [];
  item.forEach((child) => {
    if (LIST_TYPES.has(child.type.name)) {
      const kept = pruneList(child);
      if (kept) children.push(kept);
    } else {
      children.push(child);
    }
  });
  if (children.length === 1 && isEmptyParagraph(children[0])) return null;
  return item.copy(Fragment.fromArray(children));
}

/**
 * The number tiptap-markdown 0.8.10 writes for a numbered list's first item
 * (`node.attrs.start || 1`): a missing start (null, undefined, NaN) is 1.
 */
function savedStart(list: ProseMirrorNode): number {
  return (list.attrs.start as number | null | undefined) || 1;
}

/**
 * Whether `next`, a list sitting directly after `previous` (nothing between them), may be
 * one list with `previous` without changing what the writer sees. Shared by
 * `normalizeMemoDoc` (bullet lists only) and the memo editor's Backspace joins
 * (MemoListKeys), so the two cannot drift.
 *
 * - Two bullet lists: always, and they must be. tiptap-markdown writes them with one
 *   marker and a blank line between ("- a\n\n\n- b"), and that reloads as ONE loose
 *   list, so the save merges them (normalizeMemoDoc) and the editor joins them too.
 * - Two numbered lists: only when `next` continues the numbering of `previous` (its
 *   start is `previous`'s start plus its number of items; sub-items do not count). Then
 *   one list shows the same numbers. The save never merges numbered lists:
 *   tiptap-markdown writes the second of two adjacent numbered lists with ")"
 *   ("1. a\n\n\n4) b"), which reloads as two lists, each with its own start number, as
 *   the editor shows them. Only the editor's joins use this case.
 * - A bullet list and a numbered list, or anything that is not a list: never.
 */
export function storedAsOneList(previous: ProseMirrorNode, next: ProseMirrorNode): boolean {
  if (!LIST_TYPES.has(next.type.name) || previous.type !== next.type) return false;
  if (next.type.name !== "orderedList") return true;
  return savedStart(next) === savedStart(previous) + previous.childCount;
}

/**
 * The memo document as it should be stored, built as a new document (the
 * input is never mutated).
 *
 * Rules, in this order:
 * 1. Inside every bullet and ordered list (at every depth), drop list items
 *    whose only child is an empty paragraph, then drop lists left without
 *    items. Empty bullets are never stored.
 * 2. Merge two top-level BULLET lists that sit directly next to each other into one
 *    list, keeping the first list's attributes (its spacing). Stored apart they would
 *    be written with a blank line between them and reload as ONE loose list, so the
 *    saved point would not look like the editor. Numbered lists are never merged:
 *    tiptap-markdown writes the second of two with ")" and they reload as two lists,
 *    each with its own start number, as the editor shows them, also when the second
 *    continues the first's numbering (merged, the gap the editor shows was lost).
 *    Anything between them (a blank line too) keeps lists apart, and a bullet list
 *    never merges with a numbered one. Running after rule 1 means bullet lists left
 *    adjacent by a dropped list merge.
 * 3. Drop empty top-level paragraphs at the start and end of the document.
 *    Running after rule 1 means a blank line that only preceded an emptied
 *    list is trimmed as trailing.
 * 4. Replace every remaining empty top-level paragraph with a paragraph
 *    holding one `BLANK_LINE_MARKER`, so blank lines between content survive.
 *
 * "Empty paragraph" means `childCount === 0`. Only list nodes are recursed
 * into; paragraphs inside lists, quotes or tables are left as they are.
 *
 * When nothing is left, the result holds a single empty paragraph (a doc must
 * contain at least one block), which serializes to "".
 */
export function normalizeMemoDoc(doc: ProseMirrorNode): ProseMirrorNode {
  const { schema } = doc.type;

  const blocks: ProseMirrorNode[] = [];
  doc.forEach((block) => {
    const kept = LIST_TYPES.has(block.type.name) ? pruneList(block) : block;
    if (!kept) return;
    const previous = blocks[blocks.length - 1];
    // Bullet lists only (rule 2); storedAsOneList keeps the rule shared with MemoListKeys.
    if (previous && kept.type.name === "bulletList" && storedAsOneList(previous, kept)) {
      blocks[blocks.length - 1] = previous.copy(previous.content.append(kept.content));
    } else {
      blocks.push(kept);
    }
  });

  let start = 0;
  let end = blocks.length;
  while (start < end && isEmptyParagraph(blocks[start])) start++;
  while (end > start && isEmptyParagraph(blocks[end - 1])) end--;

  const paragraph = schema.nodes.paragraph;
  if (!paragraph) throw new Error("normalizeMemoDoc: schema has no paragraph node");

  const out = blocks
    .slice(start, end)
    .map((block) =>
      isEmptyParagraph(block) ? paragraph.create(null, schema.text(BLANK_LINE_MARKER)) : block,
    );
  if (out.length === 0) out.push(paragraph.create());

  return doc.copy(Fragment.fromArray(out));
}
